import { useCallback, useMemo, useState } from 'react';
import classNames from './DeleteStudentsPopup.module.scss';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { StudentReportItem } from '@/school-management/types/response';
import StudentsTable, { convertStudentReportItemToStudentsTableData } from './StudentsTable';
import DropDown, { Option } from '@/components/school-management/DropDown/DropDown';
import Button from '@/components/school-management/Button/Button';

const MAX_OTHER_REASON_LENGTH = 150;

interface ChooseReasonProps {
  students: StudentReportItem[];
  onClose: () => void;
  onReasonSelected: (selectedReason: string) => void;
}

const ChooseReason = ({ students, onClose, onReasonSelected }: ChooseReasonProps) => {
  const { t } = useAdminTranslation();
  const [selectedReason, setSelectedReason] = useState<Option>();
  const [otherReason, setOtherReason] = useState<string>('');
  const [isConfirmated, setIsConfirmated] = useState<boolean>(false);

  const studentsTableData = useMemo(() => {
    return (students || []).map(s => convertStudentReportItemToStudentsTableData(s, t));
  }, [students, t]);

  const destinationsOptions = useMemo(() => {
    return ['requestedToBeRemoved', 'aClericalError', 'other'].map(r => {
      const label = t(`studentDataModal.deleteStudentForm.reasons.${r}`);

      return {
        label: label,
        value: r,
        data: label,
      } as Option;
    });
  }, [t]);

  const optionRender = useCallback(
    (option: Option) => {
      return (
        <div className={classNames.reasonListItem}>
          <div className={classNames.reasonListItemLabel}>{option.label}</div>
        </div>
      );
    },
    [t]
  );

  const onOtherTextChange = useCallback((event: any) => {
    let value = event?.target?.value || '';

    if (value.length > MAX_OTHER_REASON_LENGTH) {
      value = value.slice(0, MAX_OTHER_REASON_LENGTH);
    }

    setOtherReason(value);
  }, []);

  return (
    <>
      <div>
        <div className={classNames.title}>{t('bulkActions.deleteStudents.chooseReason.title')}</div>
        <div className={classNames.description} style={{ maxWidth: '700px' }}>
          {t('bulkActions.deleteStudents.chooseReason.description', { num: students.length })}
        </div>
      </div>
      <StudentsTable students={studentsTableData} />
      <div>
        <div className={classNames.subTitle}>
          {t('bulkActions.deleteStudents.chooseReason.subTitle')}
        </div>
        <div className={classNames.subDescription}>
          {t('bulkActions.deleteStudents.chooseReason.subDescription')}
        </div>
        <div className={classNames.reasonSelector}>
          <DropDown
            options={destinationsOptions}
            value={selectedReason}
            onChange={setSelectedReason}
            optionRender={optionRender}
            label={`${t('bulkActions.deleteStudents.chooseReason.reason') || ''}:`}
            listClassName={classNames.reasonList}
            autoClose={true}
          />
        </div>
        {selectedReason?.value === 'other' && (
          <div className={classNames.otherReason}>
            <div className={classNames.otherReasonTitle}>
              <div>{t('bulkActions.deleteStudents.chooseReason.why')}</div>
              <div className={classNames.otherReasonTextLimit}>
                {otherReason.length}/{MAX_OTHER_REASON_LENGTH}
              </div>
            </div>
            <textarea
              className={classNames.otherReasonText}
              value={otherReason}
              onChange={onOtherTextChange}
              maxLength={MAX_OTHER_REASON_LENGTH}
            />
          </div>
        )}
        <div className={classNames.reasonConfirmation}>
          <input
            type='checkbox'
            checked={isConfirmated}
            onChange={() => setIsConfirmated(isConfirmated => !isConfirmated)}
          />
          <span>{t('bulkActions.deleteStudents.chooseReason.confirmationCheckbox')}</span>
        </div>
      </div>
      <div className={classNames.footer}>
        <Button className={classNames.textButton} onClick={onClose}>
          {t('common.back')}
        </Button>
        <Button
          className={classNames.redButton}
          disabled={
            !isConfirmated || !selectedReason || (selectedReason.value === 'other' && !otherReason)
          }
          onClick={() => {
            const reason =
              selectedReason?.value === 'other' ? otherReason : selectedReason?.data || '';
            onReasonSelected(reason);
          }}
        >
          {t('bulkActions.deleteStudents.chooseReason.submitButton')}
        </Button>
      </div>
    </>
  );
};

export default ChooseReason;
